import { useEffect, useState, useSyncExternalStore } from "react";
import { catalogicalConfigService } from "services/catalogical-config-service";

interface UseCatalogicalConfigResult<T> {
    data: T[];
    isLoading: boolean;
    error: Error | null;
}

const cache = new Map<string, unknown[]>();
const listeners = new Set<() => void>();

function notifyListeners() {
    listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

/** @internal Exposed for testing only */
export function clearCatalogicalConfigCache() {
    cache.clear();
}

export function useCatalogicalConfig<T>(domain: string): UseCatalogicalConfigResult<T> {
    const [data, setData] = useState<T[]>((cache.get(domain) as T[]) ?? []);
    const [isLoading, setIsLoading] = useState(!cache.has(domain));
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!domain) {
            setIsLoading(false);
            return;
        }

        if (cache.has(domain)) {
            setData(cache.get(domain) as T[]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        setData([]);
        setIsLoading(true);
        setError(null);

        catalogicalConfigService
            .getItems<T>(domain)
            .then((items) => {
                cache.set(domain, items);
                notifyListeners();
                if (!cancelled) {
                    setData(items);
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err);
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [domain]);

    return { data, isLoading, error };
}

/** Returns cached items for a domain. For use by non-hook utility code after the hook has populated the cache. */
export function getCachedCatalogicalItems<T>(domain: string): T[] {
    return (cache.get(domain) as T[]) ?? [];
}

const EMPTY: unknown[] = [];

/** Hook that reactively returns cached items — re-renders when cache updates. */
export function useCachedCatalogicalItems<T>(domain: string): T[] {
    return useSyncExternalStore(
        subscribe,
        () => (cache.get(domain) as T[]) ?? (EMPTY as T[]),
    );
}
